import { useEffect, useState, useCallback } from 'react';
import logger from '../../utils/logger';
import '../../styles.css';

interface PendingApprovalPageProps {
  userEmail?: string;
  onLogout: () => void;
  getIdToken?: () => Promise<string>;
}

const POLL_INTERVAL_MS = 10000; // Check every 10 seconds

const PendingApprovalPage = ({ userEmail, onLogout, getIdToken }: PendingApprovalPageProps) => {
  const [isApproved, setIsApproved] = useState(false);
  const [assignedRole, setAssignedRole] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // Function to check approval status
  const checkApprovalStatus = useCallback(async () => {
    if (!getIdToken) return;
    
    try {
      setChecking(true);
      const token = await getIdToken();
      const response = await fetch('/api/users/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const profile = await response.json();
        // Check if user is no longer pending
        if (profile.role && profile.role !== 'pending') {
          setIsApproved(true);
          setAssignedRole(profile.role);
        }
      }
    } catch (err) {
      logger.error('Error checking approval status');
    } finally {
      setChecking(false);
    }
  }, [getIdToken]);

  // Poll for approval status
  useEffect(() => {
    if (!getIdToken) return;

    // Check immediately on mount
    checkApprovalStatus();

    // Then poll periodically
    const pollInterval = setInterval(checkApprovalStatus, POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [getIdToken, checkApprovalStatus]);

  // Handle when user is approved
  const handleContinue = () => {
    // Reload the page to get full dashboard access
    window.location.reload();
  };

  // Approved state UI
  if (isApproved) {
    return (
      <div className="pending-approval-container">
        <div className="pending-approval-card approved">
          <div className="approved-icon">✓</div>
          <h1>You've Been Approved!</h1>
          
          <p className="main-message">
            Your account has been approved by an administrator.
          </p>
          
          {assignedRole && (
            <p className="role-info">
              Your role: <strong style={{ textTransform: 'capitalize' }}>{assignedRole}</strong>
            </p>
          )}
          
          <button className="wizrdom-button primary" onClick={handleContinue}>
            Continue to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Pending state UI
  return (
    <div className="pending-approval-container">
      <div className="pending-approval-card">
        <div className="pending-icon">⏳</div>
        <h1>Account Pending Approval</h1>
        
        <p className="main-message">
          Your account is pending administrator approval.
        </p>
        
        {userEmail && (
          <p className="email-info">
            Signed in as: <strong>{userEmail}</strong>
          </p>
        )}
        
        <div className="info-box">
          <h3>What happens next?</h3>
          <ul>
            <li>An administrator will review your access request</li>
            <li>You'll receive access once approved</li>
            <li>This page will automatically update when you're approved</li>
          </ul>
        </div>
        
        {checking && (
          <p className="checking-status">Checking status...</p>
        )}
        
        <p className="note">
          If you believe this is an error or need urgent access, 
          please contact your administrator.
        </p>
        
        <button className="wizrdom-button logout-button" onClick={onLogout}>
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default PendingApprovalPage;
